import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  useSetoresManejo, useProdutosManejo, useCriarLoteManejo,
  useAtividadesManejo, useProgramasAtivosPorSetores,
  useFrentesManejo, useLocaisManejo, useCriarItemLista, useEditarOperacao, useUpdateOperacaoStatus,
} from "@/hooks/use-manejo";
import { useFuncionarios } from "@/hooks/use-equipe";
import type { MnjLoteCommit, MnjOperacaoPrioridade, MnjOperacaoWithItens } from "@shared/schema";
import { formDaOperacao, bloqueiosDaEdicao, diffDaEdicao, nadaMudou, type FormOperacao } from "@shared/manejo-edicao";
import { areaAplicadaHa, ratearKgPorSetor } from "@shared/manejo-calc";
import { formatarKg, descreverEntradaManual } from "@shared/manejo-format";
import { ChevronDown, ChevronRight, Plus, Trash2, Wand2 } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { usePode } from "@/lib/permissoes";
import { hojeNaFazenda } from "@/lib/data-fazenda";

// Criação manual de operação — o caminho que não depende do interpretador (LLM).
// Confirma pelo MESMO commit do agente (POST /api/manejo/lotes → ts.createLoteManejo),
// só com origem 'manual': quantidade só em mnj_operacao_item, área congelada e
// transação única já vivem lá. Nenhum caminho de escrita novo.
//
// 🔴 O FORMULÁRIO É UMA TAREFA, NÃO UMA RECEITA.
// Até aqui, criar operação exigia escolher produto do cadastro e digitar o kg —
// e isso trancava o registro do dia a dia atrás de um cadastro de produtos que
// ninguém mantém ainda (35 linhas herdadas do Aegro, zero programa cadastrado).
// O corpo do formulário passou a ser TEXTO LIVRE: um título e as instruções de
// quem executa, onde os insumos podem ser escritos à mão ("20 kg de MAP + 15 de
// KCl"). O bloco estruturado continua existindo, fechado, para o dia em que o
// cadastro valer a pena — é ele que alimenta o rateio por hectare e o
// previsto × realizado da conclusão. Nada disso é obrigatório para criar.
//
// Uma submissão = UMA operação cobrindo os setores marcados, porque uma operação é
// uma dose única por hectare (§1.2c). O rateio ao vivo existe pra isso ficar
// visível ANTES de virar dado: se os setores marcados precisam de doses diferentes,
// o kg/ha único aparece na tela e o usuário separa em duas operações.

interface ItemForm { mnjProdutoId: string; kgTotal: string }

const ITEM_VAZIO: ItemForm = { mnjProdutoId: "", kgTotal: "" };

// Aplicação parcial não existe na operação da Fazenda Boa Vista: fertirrigação por
// gotejamento pega o setor inteiro. O campo saiu do formulário (era ruído em toda
// operação criada, por um cenário que não ocorre), mas a COLUNA continua no banco
// com default 100 — o interpretador ainda extrai percentual do texto livre.
// Operação vinda do agente
// com percentual ≠ 100 segue sendo respeitada ponta a ponta; só o formulário
// manual deixou de perguntar.
const PERCENTUAL_APLICADO_PADRAO = 100;

// ─── CRIAR UMA TAREFA É SEMPRE IGUAL, QUALQUER QUE SEJA A PORTA ─────────────────
//
// Este diálogo é a tela de criação COMPLETA; o campo "Novo item" da Lista é o atalho
// de uma linha. As duas portas pedem o mesmo (título, frente, responsável, data,
// prioridade, local) e o servidor aplica os mesmos padrões (frente, responsável da
// frente, fim da fila) pelo mesmo helper. Sem atividade do catálogo → item de lista
// (`POST /operacoes`); com atividade → o commit de sempre (`POST /lotes`).
//
// Atividade agora é OPCIONAL. Setores e insumos só aparecem quando a atividade os
// pede: fertirrigação, pulverização e foliar (dose por hectare). Atividade de
// manutenção não tem setor nem insumo. Tratos culturais e "outro" mostram os
// setores porque o servidor continua exigindo setor fora de manutenção.

/** Categorias com dose por hectare: pedem setores E aceitam insumos do cadastro. */
const CATEGORIAS_COM_DOSE = ["fertirrigacao", "pulverizacao", "foliar"];

interface NovaOperacaoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Chamado após criar — recebe a data prevista (`null` quando foi pro backlog). */
  onCriada?: (dataPrevista: string | null) => void;
  /** Pré-seleciona a data — é o `+` de uma coluna da Semana. `null` = hoje. */
  dataInicial?: string | null;
  /** Frente pré-selecionada (o chip atual da Lista). Sem ela, a única frente ativa. */
  frenteInicial?: string | null;
  /**
   * Modo EDITAR: a operação (o detalhe, com setores e insumos). O diálogo abre
   * pré-preenchido e salva só o que mudou. Sem ela, é a criação.
   */
  operacao?: MnjOperacaoWithItens | null;
}

const SEM_RESPONSAVEL = "nenhum";

export function NovaOperacaoDialog({ open, onOpenChange, onCriada, dataInicial, frenteInicial, operacao }: NovaOperacaoDialogProps) {
  const { toast } = useToast();

  const { data: atividades, isError: erroAtividades } = useAtividadesManejo();
  const { data: setores, isError: erroSetores } = useSetoresManejo();
  const { data: produtos, isError: erroProdutos } = useProdutosManejo();
  const criarLote = useCriarLoteManejo();
  const criarItem = useCriarItemLista();
  const editar = useEditarOperacao();
  const mudarStatus = useUpdateOperacaoStatus();
  const editando = !!operacao;
  // Sem `manejo.planejar` (o operador) o formulário é o SIMPLES: sem tipo de trabalho,
  // setores e insumos — a API recusa atividade para quem não planeja. O item nasce
  // atribuído ao próprio operador (some do "Minhas" dele se não for).
  const { user } = useAuth();
  const podePlanejar = usePode("manejo.planejar");
  const simples = !podePlanejar && !editando;
  const meuFuncionarioId = simples ? (user?.funcionarioId ?? null) : null;
  // Operação encerrada abre travada (só título, local, instruções e prioridade).
  const bloq = operacao ? bloqueiosDaEdicao(operacao.status) : { estrutura: false, data: false, aviso: null };
  const [setoresAbertos, setSetoresAbertos] = useState(false);
  const { data: frentesTodas = [] } = useFrentesManejo();
  const { data: funcionarios = [] } = useFuncionarios();
  const { data: locais = [] } = useLocaisManejo();
  const frentes = useMemo(() => frentesTodas.filter(f => f.ativo), [frentesTodas]);

  const [atividadeId, setAtividadeId] = useState("");
  const [frenteId, setFrenteId] = useState("");
  const [responsavelId, setResponsavelId] = useState(SEM_RESPONSAVEL);
  // O responsável acompanha a frente SÓ enquanto o usuário não mexeu no campo.
  const [responsavelTocado, setResponsavelTocado] = useState(false);
  const [prioridade, setPrioridade] = useState<MnjOperacaoPrioridade>("normal");
  const [local, setLocal] = useState("");
  const [titulo, setTitulo] = useState("");
  const [dataPrevista, setDataPrevista] = useState(hojeNaFazenda);
  // 🔴 Sem data = BACKLOG. Criar sem dia já era suportado pelo commit
  // (`dataPrevista: null`) desde o ciclo 1, mas o formulário não oferecia — então
  // o backlog só existia por API. Com a guia Backlog (ciclo 3), a opção precisa
  // estar aqui: é o caminho de "isso precisa ser feito, ainda não sei quando".
  const [semData, setSemData] = useState(false);
  const [setorIds, setSetorIds] = useState<string[]>([]);
  const [observacao, setObservacao] = useState("");
  const [itens, setItens] = useState<ItemForm[]>([ITEM_VAZIO]);
  // Fechado por padrão — e é esse default que define o formulário. Quem precisar
  // do rateio abre; quem só quer registrar a tarefa nunca vê o bloco.
  const [insumosAbertos, setInsumosAbertos] = useState(false);

  const { porSetor: programasPorSetor, isError: erroProgramas } = useProgramasAtivosPorSetores(setorIds);

  // TODOS os setores ativos entram no universo. Filtrar por `fertirrigavel` fazia
  // sentido quando a tela só criava fertirrigação; uma operação que é tarefa pode
  // ser em qualquer lugar da fazenda, e o flag continua valendo onde importa (o
  // interpretador e o programa).
  const setoresDisponiveis = useMemo(() => setores ?? [], [setores]);

  const setoresSelecionados = useMemo(
    () => setoresDisponiveis.filter(s => setorIds.indexOf(s.id) !== -1),
    [setoresDisponiveis, setorIds],
  );

  const setoresParaRateio = useMemo(
    () => setoresSelecionados.map(s => ({
      setorId: s.id,
      areaHaSnapshot: Number(s.areaHectares ?? 0),
      percentualAplicado: PERCENTUAL_APLICADO_PADRAO,
    })),
    [setoresSelecionados],
  );

  const areaAplicadaTotal = useMemo(
    () => setoresParaRateio.reduce((acc, s) => acc + areaAplicadaHa(s.areaHaSnapshot, s.percentualAplicado), 0),
    [setoresParaRateio],
  );

  const itensPreenchidos = useMemo(
    () => itens
      .map(it => ({ mnjProdutoId: it.mnjProdutoId, kgTotal: parseFloat(it.kgTotal.replace(",", ".")) }))
      .filter(it => it.mnjProdutoId && !isNaN(it.kgTotal) && it.kgTotal > 0),
    [itens],
  );

  // Um produto por operação: mnj_operacao_item tem UNIQUE (org, operação, produto) —
  // duas linhas do mesmo produto virariam erro do banco no commit. Bloqueia aqui, com
  // a razão à vista, em vez de deixar o 500 aparecer depois de preencher tudo.
  const temProdutoRepetido = new Set(itensPreenchidos.map(it => it.mnjProdutoId)).size !== itensPreenchidos.length;

  const atividadeSelecionada = (atividades ?? []).find(a => a.id === atividadeId) ?? null;
  const setoresComPrograma = programasPorSetor.filter(p => p.programa !== null);
  const nomeSetor = (id: string) => setoresDisponiveis.find(s => s.id === id)?.nome ?? "";
  const nomeProduto = (id: string) => (produtos ?? []).find(p => p.id === id)?.nome ?? "";

  // Setor sem área só atrapalha quando existe dose pra ratear — a mesma regra do
  // servidor (createLote). Sem insumo, "Outros" e "PLACA SOLAR" são destinos
  // legítimos de tarefa.
  const setoresSemArea = setoresSelecionados.filter(s => !s.areaHectares || Number(s.areaHectares) <= 0);
  const semAreaBloqueia = itensPreenchidos.length > 0 && setoresSemArea.length > 0;

  // 🔴 Só manutenção pode ficar sem setor (spec §9.5) — é a mesma regra que o
  // servidor aplica, adiantada aqui pra não virar erro depois de preencher tudo.
  // Sem atividade é item de lista: não tem setor.
  const temAtividade = !!atividadeSelecionada;
  const exigeSetor = temAtividade && atividadeSelecionada?.categoria !== "manutencao" && !bloq.estrutura;
  // Setores/insumos: já visíveis para as categorias agronômicas; para as demais ficam
  // RECOLHIDOS ("Adicionar setores e insumos") — mas se a operação já tem, abre expandido.
  const setoresObrigatorios = temAtividade && atividadeSelecionada?.categoria !== "manutencao";
  const mostraSetores = setoresObrigatorios || setoresAbertos || setorIds.length > 0;
  const comDose = temAtividade && CATEGORIAS_COM_DOSE.indexOf(atividadeSelecionada?.categoria ?? "") !== -1;
  const mostraInsumos = comDose || setoresAbertos || itensPreenchidos.length > 0 || (editando && (operacao?.itens.length ?? 0) > 0);
  // Sem tipo de trabalho a CRIAÇÃO é item de lista (sem setor); a edição aceita setores.
  const podeAdicionarSetores = !mostraSetores && (temAtividade || editando) && !bloq.estrutura;

  // Frente: obrigatória quando a org tem ao menos uma ativa (o servidor também recusa).
  const exigeFrente = frentes.length > 0;
  const tituloLimpoParaValidar = titulo.trim();

  const podeCriar =
    (temAtividade || tituloLimpoParaValidar.length > 0) &&
    (!exigeFrente || !!frenteId || bloq.estrutura) &&
    (semData || !!dataPrevista) &&
    (!exigeSetor || setoresSelecionados.length > 0) &&
    !temProdutoRepetido &&
    !semAreaBloqueia &&
    !criarLote.isPending && !criarItem.isPending && !editar.isPending && !mudarStatus.isPending;

  const resetar = () => {
    setSetoresAbertos(false);
    setFrenteId("");
    setResponsavelId(SEM_RESPONSAVEL);
    setResponsavelTocado(false);
    setPrioridade("normal");
    setLocal("");
    setAtividadeId("");
    setTitulo("");
    setDataPrevista(dataInicial ?? hojeNaFazenda());
    setSemData(false);
    setSetorIds([]);
    setObservacao("");
    setItens([ITEM_VAZIO]);
    setInsumosAbertos(false);
  };

  // O `+` de uma coluna da Semana abre o formulário já naquele dia. Reage à
  // ABERTURA, não só ao valor: reabrir noutra coluna precisa repor a data.
  useEffect(() => {
    if (!open || editando) return;
    setDataPrevista(dataInicial ?? hojeNaFazenda());
    setSemData(false);
  }, [open, dataInicial, editando]);

  // MODO EDITAR: abre pré-preenchido com tudo o que a operação tem.
  useEffect(() => {
    if (!open || !operacao) return;
    const f = formDaOperacao(operacao as any, hojeNaFazenda());
    setTitulo(f.titulo);
    setFrenteId(f.frenteId);
    setResponsavelId(f.responsavelId || SEM_RESPONSAVEL);
    setResponsavelTocado(true); // na edição o responsável NÃO acompanha a frente
    setSemData(f.semData);
    setDataPrevista(f.dataPrevista);
    setPrioridade(f.prioridade);
    setLocal(f.local);
    setAtividadeId(f.atividadeId);
    setSetorIds(f.setorIds);
    setItens(f.itens.length > 0 ? f.itens.map(i => ({ mnjProdutoId: i.mnjProdutoId, kgTotal: i.qtd })) : [ITEM_VAZIO]);
    setInsumosAbertos(f.itens.length > 0);
    setObservacao(f.observacao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, operacao?.id]);

  // Frente pré-selecionada ao abrir: o chip da Lista, ou a única frente ativa.
  useEffect(() => {
    if (!open || frenteId || editando) return;
    const inicial = frentes.find(f => f.id === frenteInicial) ?? (frentes.length === 1 ? frentes[0] : null);
    if (inicial) setFrenteId(inicial.id);
  }, [open, frenteInicial, frentes, frenteId]);

  // O responsável segue o da frente até o usuário escolher outro.
  useEffect(() => {
    if (responsavelTocado) return;
    const f = frentes.find(x => x.id === frenteId);
    setResponsavelId(meuFuncionarioId ?? f?.responsavelFuncionarioId ?? SEM_RESPONSAVEL);
  }, [frenteId, frentes, responsavelTocado, meuFuncionarioId]);

  const alternarSetor = (id: string) => {
    setSetorIds(ids => ids.indexOf(id) === -1 ? [...ids, id] : ids.filter(i => i !== id));
  };

  const atualizarItem = (idx: number, patch: Partial<ItemForm>) =>
    setItens(its => its.map((it, i) => i === idx ? { ...it, ...patch } : it));
  const adicionarItem = () => setItens(its => [...its, ITEM_VAZIO]);
  const removerItem = (idx: number) => setItens(its => its.length > 1 ? its.filter((_, i) => i !== idx) : its);

  // Pré-preenchimento pelo programa (sugestão, nunca imposição): kg_total =
  // kg/ha do programa × área aplicada TOTAL dos setores marcados — a mesma dose por
  // hectare pra todos, que é o que uma operação comporta. Com setores de programas
  // diferentes, usa o do primeiro e diz qual foi; o usuário ajusta ou separa.
  const programaBase = setoresComPrograma[0]?.programa ?? null;
  const preencherPeloPrograma = () => {
    if (!programaBase || areaAplicadaTotal <= 0) return;
    setInsumosAbertos(true);
    setItens(programaBase.itens.map(it => ({
      mnjProdutoId: it.mnjProdutoId,
      // 2 casas: a sugestão entra num campo editável, não vale carregar o resto
      // do float (20.000000000000004) pra dentro do que o usuário vai conferir.
      kgTotal: (Number(it.kgPorHa) * areaAplicadaTotal).toFixed(2),
    })));
    toast({
      title: "Preenchido pelo programa",
      description: `${programaBase.nome} (${nomeSetor(setoresComPrograma[0].setorId)}) — confira as quantidades antes de criar.`,
    });
  };

  // Salvar na EDIÇÃO: só o que mudou (`diffDaEdicao`); nada mudou = nenhuma chamada.
  // Os campos vão primeiro; a data é TRANSIÇÃO e vai depois — se ela falhar, os campos
  // já estão salvos e o aviso diz isso (não se desfaz a primeira).
  const handleSalvarEdicao = async () => {
    if (!operacao || !podeCriar) return;
    const hoje = hojeNaFazenda();
    const form: FormOperacao = {
      titulo, frenteId, responsavelId: responsavelId === SEM_RESPONSAVEL ? "" : responsavelId,
      semData, dataPrevista, prioridade, local, atividadeId, setorIds,
      itens: itensPreenchidos.map(it => ({ mnjProdutoId: it.mnjProdutoId, qtd: String(it.kgTotal) })),
      observacao,
    };
    const diff = diffDaEdicao(operacao as any, form, hoje);
    if (nadaMudou(diff)) {
      onOpenChange(false);
      return;
    }
    let camposSalvos = false;
    try {
      if (Object.keys(diff.campos).length > 0) {
        await editar.mutateAsync({ id: operacao.id, ...(diff.campos as any) });
        camposSalvos = true;
      }
      if (diff.data) {
        await mudarStatus.mutateAsync({ id: operacao.id, status: diff.data.status, dataPrevista: diff.data.dataPrevista });
      }
      toast({ title: "Operação atualizada" });
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: camposSalvos ? "Os campos foram salvos, mas a data não" : "Erro ao salvar a operação",
        description: e?.message,
        variant: "destructive",
      });
    }
  };

  const handleCriar = async () => {
    if (editando) return handleSalvarEdicao();
    if (!podeCriar) return;
    const tituloLimpo = titulo.trim();
    const observacaoLimpa = observacao.trim();
    const localLimpo = local.trim();
    const atribuidos = responsavelId === SEM_RESPONSAVEL ? undefined : [responsavelId];
    const aviso = semData ? "Operação criada no backlog" : "Operação criada";

    try {
      if (!atividadeSelecionada) {
        // Sem atividade do catálogo: item de lista, a mesma porta do campo "Novo item".
        await criarItem.mutateAsync({
          titulo: tituloLimpo,
          frenteId: frenteId || null,
          local: localLimpo || null,
          prioridade,
          dataPrevista: semData ? null : dataPrevista,
          atribuidos,
          observacao: observacaoLimpa || null,
        });
      } else {
        const payload: MnjLoteCommit = {
          origem: "manual",
          entradaRaw: descreverEntradaManual({
            atividadeNome: atividadeSelecionada.nome,
            titulo: tituloLimpo || null,
            // O recibo tem que dizer BACKLOG quando é backlog — mandar a data do
            // campo desabilitado registraria um dia que a operação não tem.
            dataPrevista: semData ? null : dataPrevista,
            setoresNomes: setoresSelecionados.map(s => s.nome),
            percentualAplicado: PERCENTUAL_APLICADO_PADRAO,
            observacao: observacaoLimpa || null,
            itens: itensPreenchidos.map(it => ({ produtoNome: nomeProduto(it.mnjProdutoId), kgTotal: it.kgTotal })),
          }),
          operacoes: [{
            atividadeId,
            // Em branco, o card cai no nome da atividade (tituloDaOperacao).
            titulo: tituloLimpo || null,
            frenteId: frenteId || null,
            atribuidos,
            local: localLimpo || null,
            prioridade,
            // `null` = nasce no BACKLOG. O servidor deriva o status daí (createLote).
            dataPrevista: semData ? null : dataPrevista,
            observacao: observacaoLimpa || null,
            setores: setoresSelecionados.map(s => ({ setorId: s.id, percentualAplicado: PERCENTUAL_APLICADO_PADRAO })),
            // `qtdPrevista` na unidade do produto — o campo se chamava kgTotalPrevisto
            // e presumia quilo em tudo (migração 052). Lista vazia é o caso comum.
            itens: itensPreenchidos.map(it => ({ mnjProdutoId: it.mnjProdutoId, qtdPrevista: it.kgTotal })),
          }],
        };
        await criarLote.mutateAsync(payload);
      }
      // A idempotência por `dedup_key` saiu com o pull do Aegro (migração 052): criar
      // duas vezes aqui cria duas operações — o que é o certo para criação manual.
      toast({ title: aviso });
      onCriada?.(semData ? null : dataPrevista);
      resetar();
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Erro ao criar operação", description: e.message, variant: "destructive" });
    }
  };

  const erroCarregamento = erroAtividades || erroSetores || erroProdutos;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) resetar(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editando ? "Editar operação" : "Nova operação"}</DialogTitle>
        </DialogHeader>

        {erroCarregamento ? (
          <p className="text-sm text-erro">
            Não foi possível carregar atividades, setores ou produtos. Recarregue a página e tente de novo.
          </p>
        ) : (
          <div className="space-y-4">
            {bloq.aviso && (
              <p className="rounded-md border border-atencao/30 bg-atencao-suave px-3 py-2 text-sm" data-testid="aviso-edicao">
                {bloq.aviso}
              </p>
            )}

            {/* O que fazer — primeiro, porque é o que a Lista mostra. Obrigatório quando
                não há atividade; com atividade, em branco o card mostra o nome dela. */}
            <div className="space-y-1">
              <Label htmlFor="op-titulo">O que fazer</Label>
              <Input
                id="op-titulo"
                value={titulo}
                onChange={e => setTitulo(e.target.value)}
                maxLength={200}
                placeholder={atividadeSelecionada ? `Ex.: ${atividadeSelecionada.nome} de sábado` : "Ex.: Entroncar o poço das placas"}
                data-testid="input-titulo"
              />
              <p className="text-xs text-muted-foreground">
                {temAtividade ? "Opcional — em branco, o card mostra o nome da atividade." : "Obrigatório quando não há tipo de trabalho."}
              </p>
            </div>

            {frentes.length > 0 && (
              <div className="space-y-1">
                <Label>Frente</Label>
                <Select value={frenteId} onValueChange={setFrenteId} disabled={bloq.estrutura}>
                  <SelectTrigger className="min-h-11" data-testid="select-frente"><SelectValue placeholder="Escolha a frente..." /></SelectTrigger>
                  <SelectContent>
                    {frentes.map(f => <SelectItem key={f.id} value={f.id}>{f.emoji ? `${f.emoji} ` : ""}{f.nome}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1">
              <Label>Responsável</Label>
              <Select
                value={responsavelId}
                onValueChange={v => { setResponsavelId(v); setResponsavelTocado(true); }}
              >
                <SelectTrigger className="min-h-11" data-testid="select-responsavel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_RESPONSAVEL}>{editando ? "Ninguém" : "Quem a frente definir"}</SelectItem>
                  {funcionarios.filter(f => f.ativo !== false).map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.apelido || f.nome}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Data prevista — percentual aplicado saiu do formulário (é sempre 100%
                aqui; ver PERCENTUAL_APLICADO_PADRAO). */}
            <div className="space-y-1.5">
              <Label>Data prevista</Label>
              <Input
                type="date"
                value={dataPrevista}
                onChange={e => setDataPrevista(e.target.value)}
                disabled={semData || bloq.data}
                className="w-full sm:w-48"
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={semData}
                  onChange={e => setSemData(e.target.checked)}
                  disabled={bloq.data}
                  className="h-4 w-4"
                  data-testid="check-backlog"
                />
                Deixar no backlog (sem data)
              </label>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Prioridade</Label>
                <Select value={prioridade} onValueChange={v => setPrioridade(v as MnjOperacaoPrioridade)}>
                  <SelectTrigger className="min-h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="baixa">Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="op-local">Local</Label>
                <Input
                  id="op-local" list="op-locais" value={local} onChange={e => setLocal(e.target.value)}
                  maxLength={120} placeholder="Ex.: poço das placas" className="min-h-11" data-testid="input-local"
                />
                <datalist id="op-locais">{locais.map(l => <option key={l} value={l} />)}</datalist>
              </div>
            </div>

            {/* Tipo de trabalho (atividade do catálogo) — OPCIONAL. Sem ela é item de
                lista; com ela, setores/insumos aparecem conforme a categoria. */}
            {!simples && (
            <div className="space-y-1">
              <Label>Tipo de trabalho (opcional)</Label>
              <select
                className="h-11 w-full rounded-md border border-input bg-background px-2 text-sm"
                value={atividadeId}
                onChange={e => setAtividadeId(e.target.value)}
                disabled={bloq.estrutura}
                data-testid="select-atividade"
              >
                <option value="">Nenhum — só a tarefa</option>
                {(atividades ?? []).map(a => <option key={a.id} value={a.id}>{a.nome}</option>)}
              </select>
            </div>
            )}

            {podeAdicionarSetores && (
              <Button type="button" variant="outline" className="min-h-11 w-full" onClick={() => setSetoresAbertos(true)} data-testid="btn-adicionar-setores">
                <Plus className="h-4 w-4 mr-1" />Adicionar setores e insumos
              </Button>
            )}

            {mostraSetores && (
              <fieldset disabled={bloq.estrutura} className="min-w-0 space-y-4 border-0 p-0 m-0">
            {/* Setores */}
            <div className="space-y-1.5">
              <Label>Setores{exigeSetor ? "" : " (opcional)"}</Label>
              {setoresDisponiveis.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Nenhum setor ativo cadastrado.
                </p>
              ) : (
                /* Chips em linha, só com o NOME. A grade de 2 colunas com a área
                   embaixo de cada setor gastava 8 linhas da caixa e empurrava as
                   instruções pra fora da primeira tela — e a área só importa quando
                   existe dose pra ratear, que é onde ela aparece (resumo abaixo e
                   bloco de insumos). */
                <div className="flex flex-wrap gap-1.5">
                  {setoresDisponiveis.map(s => {
                    const marcado = setorIds.indexOf(s.id) !== -1;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => alternarSetor(s.id)}
                        className={`rounded-md border px-2.5 py-1.5 text-sm transition-colors ${
                          marcado ? "border-primary bg-primary/10 font-semibold" : "hover:bg-muted/40"
                        }`}
                        data-testid={`setor-${s.id}`}
                      >
                        {s.nome}
                      </button>
                    );
                  })}
                </div>
              )}
              {setoresSelecionados.length > 0 && areaAplicadaTotal > 0 && (
                <p className="text-xs text-muted-foreground tabular-nums">
                  {formatarKg(areaAplicadaTotal)} ha marcados
                </p>
              )}
              {!exigeSetor && setoresSelecionados.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Manutenção pode ficar sem setor (bomba, sede, adutora).
                </p>
              )}
            </div>
              </fieldset>
            )}

            {/* Instruções — é aqui que os insumos são escritos à mão enquanto o
                cadastro de produtos não for prioridade. */}
            <div className="space-y-1">
              <Label htmlFor="op-observacao">Instruções para quem executa</Label>
              <Textarea
                id="op-observacao"
                rows={3}
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
                maxLength={2000}
                placeholder="Produtos, doses, ferramentas, cuidados. Ex.: 20 kg de MAP + 15 kg de KCl no tanque, abrir o registro do A5 antes."
                data-testid="input-observacao"
              />
            </div>

            {mostraInsumos && (
              <fieldset disabled={bloq.estrutura} className="min-w-0 space-y-4 border-0 p-0 m-0">
            {/* Insumos do cadastro — OPCIONAL e fechado. Só quem quer o rateio por
                hectare e o previsto × realizado da conclusão precisa abrir. */}
            <div className="rounded-md border">
              <button
                type="button"
                onClick={() => setInsumosAbertos(a => !a)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted/40"
                data-testid="toggle-insumos"
              >
                <span className="flex items-center gap-1.5">
                  {insumosAbertos ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Insumos do cadastro
                  <span className="text-xs text-muted-foreground">(opcional)</span>
                </span>
                {itensPreenchidos.length > 0 && (
                  <Badge variant="secondary" className="font-normal">{itensPreenchidos.length}</Badge>
                )}
              </button>

              {insumosAbertos && (
                <div className="space-y-2 border-t p-3">
                  <p className="text-xs text-muted-foreground">
                    Só para quem mantém o cadastro de produtos — é o que habilita a dose por hectare e a
                    conferência do realizado. As doses escritas nas instruções acima já bastam para o campo.
                  </p>

                  <div className="flex items-center justify-end gap-1">
                    {programaBase && (
                      <Button
                        variant="ghost" size="sm" className="h-7 text-xs"
                        onClick={preencherPeloPrograma}
                        disabled={areaAplicadaTotal <= 0}
                        title={`Preencher a partir do programa ${programaBase.nome}`}
                      >
                        <Wand2 className="h-3.5 w-3.5 mr-1" />Pelo programa
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={adicionarItem}>
                      <Plus className="h-3.5 w-3.5 mr-1" />Item
                    </Button>
                  </div>

                  {erroProgramas && (
                    <p className="text-xs text-atencao">
                      Não foi possível checar o programa dos setores — o pré-preenchimento está indisponível. Dá pra informar as quantidades à mão.
                    </p>
                  )}
                  {setoresComPrograma.length > 1 && (
                    <p className="text-xs text-muted-foreground">
                      {setoresComPrograma.length} setores marcados têm programa ativo — o pré-preenchimento usa o de{" "}
                      {nomeSetor(setoresComPrograma[0].setorId)} (uma operação carrega uma dose por hectare só).
                    </p>
                  )}

                  {itens.map((it, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <select
                        className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm"
                        value={it.mnjProdutoId}
                        onChange={e => atualizarItem(idx, { mnjProdutoId: e.target.value })}
                      >
                        <option value="">Produto...</option>
                        {(produtos ?? []).map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
                      </select>
                      <Input
                        type="number" step="0.01" min="0" className="w-28" placeholder="quantidade"
                        value={it.kgTotal}
                        onChange={e => atualizarItem(idx, { kgTotal: e.target.value })}
                      />
                      <Button variant="ghost" size="icon" onClick={() => removerItem(idx)} disabled={itens.length === 1}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  {temProdutoRepetido && (
                    <p className="text-xs text-erro">
                      O mesmo produto aparece em mais de uma linha — some as quantidades numa linha só.
                    </p>
                  )}

                  {/* Rateio ao vivo — kg/ha e kg por setor, sempre DERIVADOS (nunca gravados) */}
                  <RateioAoVivo
                    areaAplicadaTotal={areaAplicadaTotal}
                    setoresParaRateio={setoresParaRateio}
                    itens={itensPreenchidos}
                    nomeSetor={nomeSetor}
                    nomeProduto={nomeProduto}
                  />
                </div>
              )}
            </div>
              </fieldset>
            )}

            {/* O único erro que trava a criação e não é visível no bloco fechado. */}
            {semAreaBloqueia && (
              <p className="text-xs text-erro">
                {setoresSemArea.map(s => s.nome).join(", ")} não {setoresSemArea.length > 1 ? "têm" : "tem"} área
                cadastrada — sem área não há dose por hectare. Tire os insumos do cadastro ou cadastre a área em
                Manejo &gt; Programa &gt; Setores.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleCriar} disabled={!podeCriar} data-testid="btn-criar-operacao">
            {editando ? (editar.isPending ? "Salvando..." : "Salvar") : (criarLote.isPending || criarItem.isPending ? "Criando..." : "Criar operação")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface RateioAoVivoProps {
  areaAplicadaTotal: number;
  setoresParaRateio: Array<{ setorId: string; areaHaSnapshot: number; percentualAplicado: number }>;
  itens: Array<{ mnjProdutoId: string; kgTotal: number }>;
  nomeSetor: (id: string) => string;
  nomeProduto: (id: string) => string;
}

// O que a invariante nº 3 significa na prática, na tela: um kg/ha único pra todos os
// setores marcados. Números derivados por ratearKgPorSetor (@shared/manejo-calc), a
// MESMA função usada pra conferir o rateio fora da tela — o componente não faz conta
// por conta própria.
function RateioAoVivo({ areaAplicadaTotal, setoresParaRateio, itens, nomeSetor, nomeProduto }: RateioAoVivoProps) {
  if (setoresParaRateio.length === 0 || itens.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        Marque os setores e informe a quantidade total para ver a dose por hectare e o rateio por setor.
      </p>
    );
  }

  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">Rateio (derivado)</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatarKg(areaAplicadaTotal)} ha aplicados · {setoresParaRateio.length} setor(es)
        </span>
      </div>

      {itens.map((item, idx) => {
        const rateio = ratearKgPorSetor(item.kgTotal, setoresParaRateio);
        return (
          <div key={idx} className="space-y-1">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium truncate">{nomeProduto(item.mnjProdutoId)}</span>
              <span className="tabular-nums">
                {formatarKg(item.kgTotal)} kg ·{" "}
                <span className="font-semibold">
                  {rateio.kgPorHa === null ? "—" : `${formatarKg(rateio.kgPorHa)} kg/ha`}
                </span>
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {rateio.porSetor.map(s => (
                <Badge key={s.setorId} variant="secondary" className="text-[11px] font-normal tabular-nums">
                  {nomeSetor(s.setorId)} {formatarKg(s.kg)} kg
                </Badge>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
